<script lang="ts" setup>
import { FetchError } from 'ofetch'
import type { DocumentToDownload, Field, FieldData } from '~~/server/api/forms/form'

export interface FormKitWrapperProps {
    formId: string
    fields: Field[]
    downloadDocument?: DocumentToDownload
}

const { $csrfFetch }: any = useNuxtApp()
const isLoading = ref<boolean>(false)
const formSent = ref<boolean>(false)
const props = defineProps<FormKitWrapperProps>()
const isError = ref<boolean>(false)
const config = useRuntimeConfig()
const errors = ref<FormKitErrorMessage[]>([])
const errorStatusCode = ref<number>()
const wrapperElement = ref<HTMLElement>()
const successMessageElement = ref<HTMLElement>()
const errorMessageElement = ref<HTMLElement>()
const formkitErrors = ref<string[]>()
// Screen reader announcement message - changes trigger aria-live announcement
const screenReaderAnnouncement = ref<string>('')
const router = useRouter()
const { data, sendEvent } = useTracker()

const rows = computed(() => {
    return props.fields[0]?.groups[0]?.rows || []
})

// Reliable announce function using non-breaking space trick so DOM always changes.
// NVDA, JAWS and VoiceOver all require an actual text node change to trigger aria-live.
// Empty string → empty string is NOT a change; '\u00A0' → real message IS a change.
// Focus is delayed 150ms after the announcement to prevent competition between
// aria-live and focus events (screen readers announce one or the other, not both).
const announce = (message: string, delayFocus?: () => void): void => {
    screenReaderAnnouncement.value = '\u00A0' // non-breaking space — forces DOM change
    nextTick(() => {
        screenReaderAnnouncement.value = message
        if (delayFocus) {
            setTimeout(delayFocus, 150)
        }
    })
}

const handleSubmit = (rawBody: any): void => {
    // disable form
    isLoading.value = true
    // get captcha token
    grecaptcha.ready(() => {
        grecaptcha
            .execute(config.public.RECAPTCHA_SITE_KEY, {
                action: 'submit',
            })
            .then(async (token: string) => {
                if (token) {
                    submitData({ rawBody, token })
                    return
                }
                // captcha verification failed
                formSent.value = true
                isLoading.value = false
            })
    })
}

const submitData = async ({
    rawBody,
    token,
}: {
    rawBody: any
    token: string
}): Promise<void> => {
    isError.value = false
    // init form body
    const body = new FormData()
    // set body data
    const keys = Object.keys(rawBody)
    for (const key of keys) {
        // some inputs has special array format
        if (Array.isArray(rawBody[key])) {
            // map over all input in array
            const arrayLength = rawBody[key].length
            rawBody[key].forEach((input: HTMLFormElement, index: number) => {
                // file input has special format
                if (Object.prototype.hasOwnProperty.call(input, 'file')) {
                    const bodyKey = `${key}[file]${arrayLength > 1 ? `[${index}]` : ''}`
                    body.append(bodyKey, input.file)
                }
            })
            // we added key in the prev section, don't add the key again
            if (body.has(key)) continue
        }
        // append input to the body
        body.append(key, rawBody[key] ?? '')
    }

    // send data
    await $csrfFetch('/api/forms/form/send', {
        method: 'POST',
        body,
        params: {
            token,
        },
    }).catch((error: FetchError): void => {

        if (error.statusCode) errorStatusCode.value = error.statusCode

        // save error messages
        errors.value = Array.isArray(error.data) ? error.data : [error.data]
        formkitErrors.value = ['error']
        // show error
        isError.value = true

        // Scroll first (separate nextTick so it doesn't block the announce)
        nextTick(() => {
            if (wrapperElement.value) {
                useScrollIntoView(wrapperElement.value)
            }
        })

        // Build error message string, then announce.
        // Focus is delayed 150ms after announcement via the announce() helper
        // so the screen reader doesn't have to choose between aria-live and focus.
        const errorMessages = errors.value?.map((e: FormKitErrorMessage) => e.message).join('. ') || ''
        announce(
            `Formulár sa nepodarilo odoslať. ${errorMessages}`,
            () => {
                if (errorMessageElement.value) {
                    errorMessageElement.value.focus()
                }
            }
        )
    })

    if (!isError.value) {
        // show success message
        formSent.value = true
        // empty errors
        errors.value = []

        // Scroll first
        nextTick(() => {
            if (wrapperElement.value) {
                useScrollIntoView(wrapperElement.value)
            }
        })

        // Delay announcement to let the DOM settle after major changes (form removal + success insertion).
        // Screen readers need time to process DOM mutations before picking up aria-live updates (WCAG 4.1.3).
        setTimeout(() => {
            announce(
                'Formulár bol úspešne odoslaný.',
                () => {
                    if (successMessageElement.value) {
                        successMessageElement.value.focus()
                    }
                }
            )
        }, 300)

        // send data
        sendEvent({
            data: {
                ...data,
                click: 'form sent',
            },
        })
    }
    // disable loading
    isLoading.value = false
}

const getType = (type: string): string => {
    if (['checkbox_multiple', 'checkbox_single'].includes(type))
        return 'checkbox'
    if (type === 'telephone') return 'tel'
    if (type === 'agreement_number') return 'text'

    return type
}

const handleNewForm = (): void => {
    router.go(0)
}

const showErrors = (): void => {
    if (!wrapperElement.value) return

    const firstErrorField = wrapperElement.value.querySelector(
        '[data-invalid="true"]'
    ) as HTMLElement

    // Announce first, then scroll + focus after 150ms delay.
    // Previously scroll and focus happened simultaneously with aria-live,
    // causing screen readers to skip the announcement. (WCAG 3.3.1)
    announce(
        'Formulár obsahuje chyby. Skontrolujte prosím vyplnené údaje.',
        () => {
            if (firstErrorField) {
                useScrollIntoView(firstErrorField)
                // Try to find a visible input inside the invalid field
                let focusTarget = firstErrorField.querySelector('input:not([type="hidden"]), select, textarea') as HTMLElement
                if (!focusTarget) {
                    // Custom components (Select, Date, etc.) use FormKit type="hidden"
                    // The actual focusable element (button) is in the parent wrapper
                    const wrapper = firstErrorField.parentElement
                    if (wrapper) {
                        focusTarget = wrapper.querySelector('button, input:not([type="hidden"]), select, textarea') as HTMLElement
                    }
                }
                if (focusTarget) {
                    focusTarget.focus()
                }
            }
        }
    )
}

// Set upper date
const upperDate = computed((): Date => {
    const date = new Date()
    date.setFullYear(date.getFullYear() - 15)
    return date
})

const minYear = 1920
const lowerDate = computed((): Date => {
    const date = new Date()
    date.setFullYear(minYear)
    return date
})

const getValidations = (field: FieldData): string => {
    const type = getType(field.type)
    if(type !== 'checkbox') {
        return field.validations || ''
    }
    return `${field.validations}${field.validations?.includes('required') ? '|accepted' : ''}`
}

useHead({
    script: [
        {
            src: `https://www.google.com/recaptcha/api.js?render=${config.public.RECAPTCHA_SITE_KEY}`,
            defer: true,
        },
    ],
})

</script>
<template>
  <div
    ref="wrapperElement"
    class="scroll-margin-top-8"
  >
    <!-- Accessibility: Live region for screen reader announcements (WCAG 4.1.3) -->
    <!-- Must be present in DOM before content changes for announcements to work. -->
    <!-- Uses non-breaking space trick in script to guarantee DOM node change on every update. -->
    <div
      aria-live="assertive"
      aria-atomic="true"
      class="visually-hidden"
    >
      {{ screenReaderAnnouncement }}
    </div>

    <!-- Accessibility: role="status" announces successful form submission to screen readers (WCAG 4.1.3) -->
    <!-- tabindex="-1" allows programmatic focus for screen reader users -->
    <div
      v-if="formSent"
      ref="successMessageElement"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      tabindex="-1"
      class="focus-visible-outline"
    >
      <Alert
        color="success"
        class="text-center"
      >
        {{
          $t(
            'Formulár bol úspešne odoslaný.'
          )
        }}
      </Alert>
      <div class="d-flex justify-content-center mt-4">
        <VButton
          color="link"
          @click="handleNewForm"
        >
          {{ $t('Nový formulár') }}
        </VButton>
      </div>
    </div>

    <div
      v-if="errors?.length > 0"
      ref="errorMessageElement"
      tabindex="-1"
      class="focus-visible-outline"
    >
      <LazyFormKitWrapperError
        :errors="errors"
        :status-code="errorStatusCode"
      />
    </div>

    <FormKit
      v-if="!formSent"
      type="form"
      method="POST"
      :actions="false"
      :disabled="isLoading"
      :config="{ validationVisibility: 'blur' }"
      @submit="handleSubmit"
      @submit-invalid="showErrors"
    >
      <FormKit
        type="hidden"
        name="formId"
        :value="props.formId"
      />
      <div
        v-for="(row, index) in rows"
        :key="index"
        class="row"
      >
        <template
          v-for="(field, index2) in row"
          :key="index2"
        >
          <div
            v-if="field"
            class="col-12"
            :class="`col-md-${field.size}`"
          >
            <template v-if="field.type === 'note'">
              <FormKitCustomNote :value="field.value || ''" />
            </template>
            <template v-else-if="field.type === 'document'">
              <FormKitCustomDownload
                v-if="field.document"
                :file="field.document"
              />
            </template>
            <template v-else-if="field.type === 'person_client_type'">
              <FormKitCustomUserSwitchClientCompany />
            </template>
            <template v-else-if="field.type === 'person_type'">
              <FormKitCustomUserSwitchCompany />
            </template>
            <template v-else-if="field.type === 'client_type'">
              <FormKitCustomUserSwitchClient />
            </template>
            <template v-else-if="field.type === 'gdpr'">
              <FormKitCustomGdpr :data="field" />
            </template>
            <template v-else-if="field.type === 'date'">
              <div class="formkit-outer">
                <FormKitCustomDate
                  :data="{
                    name: field.name,
                    type: 'text',
                    label: field.label,
                    validations: field.validations
                  }"
                  :show-reset-btn="false"
                  :upper-limit="upperDate"
                  :lower-limit="lowerDate"
                  :start-view="'year'"
                />
              </div>
            </template>
            <template v-else-if="field.type === 'select'">
              <div class="formkit-outer">
                <FormKitCustomSelect
                  :data="field"
                  show-label
                />
              </div>
            </template>
            <template v-else-if="field.type === 'range'">
              <FormKitCustomRange :data="field" />
            </template>
            <template
              v-else-if="['submit', 'button'].includes(field.type)"
            >
              <FormKitCustomButton
                :loading="isLoading"
                :data="field"
              />
            </template>
            <FormKitCustomOptions
              v-else-if="field.list_options && field.type === 'checkbox'"
              :data="field"
            />
            <FormKitCustomRadioOptions
              v-else-if="field.list_options && field.type === 'radio'"
              :data="field"
            />
            <FormKitCustomFile
              v-else-if="field.type === 'file'"
              :data="field"
            />
            <FormKitCustomTextarea
              v-else-if="field.type === 'textarea'"
              :data="field"
            />
            <FormKitCustomTel
              v-else-if="field.type === 'tel'"
              :data="field"
            />
            <FormKitCustomDivider
              v-else-if="field.type === 'horizontal_rule'"
            />
            <FormKit
              v-else
              :label="field.label"
              :help="field.help"
              :name="field.name"
              :placeholder="field.placeholder"
              :validation="getValidations(field)"
              :validation-messages="{
                ...field.validation_messages,
                required: field.validation_messages?.['required'] || `${field.label} ${$t('je povinný údaj')}`,
              }"
              :delay="600"
              :type="getType(field.type) as any"
            />
          </div>
        </template>
      </div>
      <FormKitCustomReCaptchaText />
    </FormKit>
  </div>
</template>
<style lang="scss">
@import './FormKitWrapper.vars.scss';
</style>
<style lang="scss">
@import './FormKitWrapper.scss';
</style>
